/**
 * QueryParamTransformer - A utility for transforming URLs with query parameters
 * Allows configuration-driven transformation of URLs based on current page query parameters
 */
class QueryParamTransformer {
  constructor(transformations = []) {
    this.transformations = transformations;
    this.currentParams = new URLSearchParams(window.location.search);
    this.init();
  }

  /**
   * Initialize the transformer
   */
  init() {
    if (this.transformations.length === 0) {
      return;
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.processLinks());
    } else {
      this.processLinks();
    }

    // Also process any dynamically added links
    this.observeChanges();
  }

  /**
   * Process all links on the page
   */
  processLinks() {
    const links = document.querySelectorAll('a[href]');
    links.forEach((link) => this.processLink(link));
  }

  /**
   * Process a single link element
   * @param {HTMLAnchorElement} link - The link element to process
   */
  processLink(link) {
    const originalHref = link.getAttribute('href');
    if (!originalHref) return;

    let modifiedHref = originalHref;
    let wasModified = false;
    const appliedParams = new Set(); // Track which parameters have been applied

    // Apply each transformation in sequence
    this.transformations.forEach((transformation) => {
      const result = this.applyTransformation(modifiedHref, transformation, appliedParams);
      if (result !== modifiedHref) {
        modifiedHref = result;
        wasModified = true;
        // Track that this parameter was applied
        appliedParams.add(transformation.targetParam);
      }
    });

    // Update the link if it was modified
    if (wasModified) {
      link.setAttribute('href', modifiedHref);
      // Store original href for reference if needed
      if (!link.hasAttribute('data-original-href')) {
        link.setAttribute('data-original-href', originalHref);
      }
    }
  }

  /**
   * Apply a single transformation to a URL
   * @param {string} url - The URL to transform
   * @param {Object} transformation - The transformation configuration
   * @param {Set} appliedParams - Set of parameters that have already been applied
   * @returns {string} - The transformed URL
   */
  applyTransformation(url, transformation, appliedParams = new Set()) {
    const { targetUrls, sourceParam, targetParam, defaultValue, isPageDefault } = transformation;

    // Check if this URL matches any of the target URLs
    const matchesTarget = targetUrls.some((targetUrl) => {
      // Handle relative URLs and absolute URLs
      const normalizedUrl = this.normalizeUrl(url);
      const normalizedTarget = this.normalizeUrl(targetUrl);
      return normalizedUrl.includes(normalizedTarget) || normalizedUrl === normalizedTarget;
    });

    if (!matchesTarget) {
      return url;
    }

    // For page defaults, don't apply if the parameter was already set by another transformation
    if (isPageDefault && appliedParams.has(targetParam)) {
      return url;
    }

    // Check if we have the source parameter in current URL (skip for page defaults with empty sourceParam)
    let valueToUse = sourceParam ? this.currentParams.get(sourceParam) : null;

    // If no source value and we have a default value, use the default
    if (!valueToUse && defaultValue) {
      valueToUse = defaultValue;
    }

    // If still no value to use, skip this transformation
    if (!valueToUse) {
      return url;
    }

    // Parse the URL and add/update the target parameter
    try {
      const urlObj = new URL(url, window.location.origin);

      // For page defaults, only set if the parameter doesn't already exist
      if (isPageDefault && urlObj.searchParams.has(targetParam)) {
        return url;
      }

      urlObj.searchParams.set(targetParam, valueToUse);

      // Return relative URL if original was relative
      if (url.startsWith('/') || !url.includes('://')) {
        return urlObj.pathname + urlObj.search + urlObj.hash;
      }

      return urlObj.toString();
    } catch (error) {
      console.warn('QueryParamTransformer: Error processing URL:', url, error);
      return url;
    }
  }

  /**
   * Normalize URL for comparison
   * @param {string} url - The URL to normalize
   * @returns {string} - The normalized URL
   */
  normalizeUrl(url) {
    // Remove leading/trailing slashes and convert to lowercase for comparison
    return url.toLowerCase().replace(/^\/+|\/+$/g, '');
  }

  /**
   * Observe DOM changes to process new links
   */
  observeChanges() {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Process the node if it's a link
            if (node.tagName === 'A' && node.hasAttribute('href')) {
              this.processLink(node);
            }
            // Process any links within the added node
            const links = node.querySelectorAll && node.querySelectorAll('a[href]');
            if (links) {
              links.forEach((link) => this.processLink(link));
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Add a new transformation
   * @param {Object} transformation - The transformation configuration
   */
  addTransformation(transformation) {
    this.transformations.push(transformation);
    this.processLinks(); // Re-process all links
  }

  /**
   * Remove all transformations
   */
  clearTransformations() {
    this.transformations = [];
  }

  /**
   * Get current transformations
   * @returns {Array} - Current transformations
   */
  getTransformations() {
    return [...this.transformations];
  }
}

// Global instance for easy access
window.QueryParamTransformer = QueryParamTransformer;

// Initialize with empty transformations by default
window.queryParamTransformer = new QueryParamTransformer();
